import React, { ChangeEvent, Component } from "react";
import equal from "fast-deep-equal";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { IOrganization } from "@formant/core/model/IOrganization";
import { IView } from "@formant/core/model/IView";
import { LoadingIndicator } from "@formant/ui/component/common/LoadingIndicator";

import {
    appStore,
    authStore,
    organizationStore,
    viewStore,
} from "../../../stores";
import { ButtonsBar } from "../../common/ButtonsBar";
import { DataTable } from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { Help } from "../../common/Help";
import { NumberField } from "../../common/NumberField";
import { Scrollable } from "../../common/Scrollable";
import { Section } from "../../common/Section";
import { SelectField } from "../../common/SelectField";
import { StandardButton } from "../../common/StandardButton";
import { Switch } from "../../common/Switch";
import { RowCheck } from "../RowCheck";
import { RowDate } from "../RowDate";
import { RowTitle } from "../RowTitle";
import { SettingsPage } from "../SettingsPage";
import * as styles from "./index.scss";

@observer
export class Overview extends Component {
    @observable
    private loading: boolean = false;
    @observable
    private lastSavedOrganization?: IOrganization;
    @observable
    private organization?: IOrganization;
    @observable
    private organizationId = authStore.organizationId || "";

    private primaryModeOptions: string[] = [];

    public componentDidMount() {
        const organization = organizationStore.get(this.organizationId);
        if (!organization) {
            return false;
        }

        this.organization = organization;
        this.lastSavedOrganization = organization;
    }

    public render() {
        const { organization } = this;

        if (!organization) {
            return <LoadingIndicator visible={true} />;
        }

        const dirty = !equal(organization, this.lastSavedOrganization);
        const {
            overviewSecondaryMode,
            overviewShowSidebar,
            overviewSidebarClosed,
            overviewShowFilters,
        } = organization.overviewSettings || {};

        const views = viewStore.list() || [];
        const customViews = views.filter(
            _ => _.url && Object.keys(_.filter || {}).length === 0,
        );
        const overviewCustomViews = customViews.filter(
            _ => _.showOnMultiDevice,
        );

        this.primaryModeOptions = [...overviewCustomViews.map(_ => _.name)];
        this.primaryModeOptions.push("List");
        if (defined(organization).mapViewEnabled) {
            this.primaryModeOptions.push("Map");
        }

        const columns: IDataTableColumn<IView>[] = [
            {
                header: "Name",
                row: _ => (
                    <RowTitle
                        title={_.name}
                        icon={_.url ? "utility" : "view"}
                    />
                ),
            },
            {
                header: "Created",
                row: _ => <RowDate date={_.createdAt} />,
                hideBelow: "md",
            },
            {
                header: "Updated",
                row: _ => <RowDate date={_.updatedAt} />,
                hideBelow: "md",
            },
            {
                header: "Show on Overview Page",
                row: _ => (
                    <RowCheck
                        checked={_.showOnMultiDevice || false}
                        onChange={async j => {
                            if (_.id) {
                                await viewStore.update(_.id, {
                                    ..._,
                                    showOnMultiDevice: j,
                                });
                            }
                        }}
                    />
                ),
            },
        ];

        return (
            <div className="p-6">
                <Scrollable>
                    <SettingsPage
                        className={styles["overview-settings"]}
                        title="Overview"
                        extra={
                            <Help
                                text="A view is a group of visualization modules within the device channel. You can add, edit or delete views, and you can apply views to new or existing devices."
                                to="https://docs.formant.io/docs/custom-web-views"
                            />
                        }
                    >
                        <Section title="Primary Mode">
                            <DataTable
                                columns={columns}
                                rows={customViews}
                                loading={views === undefined}
                            />
                            <Switch
                                state={!!organization.mapViewEnabled}
                                label="Enable Map View"
                                info="Enable the map view on the overview page."
                                stateLabels={["Off", "On"]}
                                onChange={this.onChangeMapView}
                            />
                            <NumberField
                                label="Seconds between health checks"
                                numberValue={organization.onlineSensitivity}
                                onChangeNumber={this.onChangeOnlineSensitivity}
                            />
                            <Switch
                                state={overviewShowFilters ?? true}
                                label="Show Filters"
                                info="Show the filters on the overview page."
                                stateLabels={["Off", "On"]}
                                onChange={this.onChangeShowFilters}
                            />
                        </Section>
                        <Section title="Secondary Mode">
                            <p>
                                The secondary mode is the view that is shown in
                                the sidebar when the sidebar is open.
                            </p>
                            <Switch
                                state={overviewShowSidebar || false}
                                label="Show Sidebar"
                                info="Show the sidebar on the overview page."
                                stateLabels={["Off", "On"]}
                                onChange={this.onChangeShowSidebar}
                            />

                            {overviewShowSidebar && (
                                <>
                                    <p>
                                        Choose the default view when the sidebar
                                        is open and its initial state.
                                    </p>
                                    <Switch
                                        state={overviewSidebarClosed || false}
                                        label="Sidebar Closed by Default"
                                        info="Set the sidebar to be opened or closed by default."
                                        stateLabels={["Off", "On"]}
                                        onChange={this.onChangeSidebarClosed}
                                    />
                                    <SelectField
                                        label="Overview Secondary Mode"
                                        value={overviewSecondaryMode}
                                        onChange={this.onChangeSecondaryMode}
                                    >
                                        <option value="Groups">Groups</option>
                                        <option value="Devices">Devices</option>
                                        <option value="Notifications">
                                            Notifications
                                        </option>
                                    </SelectField>
                                </>
                            )}
                        </Section>
                    </SettingsPage>
                </Scrollable>
                <ButtonsBar fixed={true} collapsed={!dirty}>
                    <StandardButton
                        onClick={this.push}
                        large={true}
                        primary={true}
                        loading={this.loading}
                    >
                        Save
                    </StandardButton>
                </ButtonsBar>
            </div>
        );
    }

    private onChangeMapView = (enabled: boolean) => {
        defined(this.organization).mapViewEnabled = enabled;
    };

    private onChangeShowSidebar = (enabled: boolean) => {
        defined(this.organization).overviewSettings = {
            ...defined(this.organization).overviewSettings,
            overviewShowSidebar: enabled,
        };
    };

    private onChangeSidebarClosed = (enabled: boolean) => {
        defined(this.organization).overviewSettings = {
            ...defined(this.organization).overviewSettings,
            overviewSidebarClosed: enabled,
        };
    };

    private onChangeSecondaryMode = (e: ChangeEvent<HTMLSelectElement>) => {
        defined(this.organization).overviewSettings = {
            ...defined(this.organization).overviewSettings,
            overviewSecondaryMode: e.target.value,
        };
    };

    private onChangeOnlineSensitivity = (value: number | undefined) => {
        if (value) {
            defined(this.organization).onlineSensitivity = value;
        }
    };

    private onChangeShowFilters = (enabled: boolean) => {
        defined(this.organization).overviewSettings = {
            ...defined(this.organization).overviewSettings,
            overviewShowFilters: enabled,
        };
    };

    private push = async () => {
        try {
            const { organizationId } = this;
            const {
                overviewPrimaryMode,
                overviewSecondaryMode,
                overviewShowSidebar,
                overviewSidebarClosed,
                overviewSettingsEnabled,
                overviewShowFilters,
            } = this.organization?.overviewSettings || {};
            if (!this.organization) {
                return;
            }

            this.loading = true;
            const validPrimaryMode = this.primaryModeOptions.includes(
                `${overviewPrimaryMode}`,
            );

            await organizationStore.update(organizationId, {
                overviewSettings: {
                    overviewPrimaryMode: validPrimaryMode
                        ? overviewPrimaryMode
                        : this.primaryModeOptions[0] ||
                          "No primary mode available",
                    overviewSecondaryMode,
                    overviewShowSidebar,
                    overviewSidebarClosed,
                    overviewSettingsEnabled,
                    overviewShowFilters,
                },
                mapViewEnabled: this.organization.mapViewEnabled,

                listViewEnabled: this.organization.listViewEnabled,
                onlineSensitivity: this.organization.onlineSensitivity,
            });

            const organization = organizationStore.get(organizationId);

            if (organization) {
                this.organization = organization;
                this.lastSavedOrganization = organization;
            }

            this.loading = false;
        } catch (error) {
            const responseError = narrowError(error, ResponseError);

            Object.values(responseError.validationErrors).forEach(message =>
                appStore.setSnackbar(message),
            );
            this.loading = false;
        }
    };
}
